import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import { EventItemCard } from '@apps-common/apps/portal/pages/units/pages/manage/pages/events/components'
import { HubConnectionStatus } from '@apps-common/components/common'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { PageContentContainerInline } from '@apps-common/components/layout'
import { commonIcons } from '@apps-common/config/icons'
import { motionSlideDownFadeIn } from '@apps-common/config/motion-variants'
import { SignalRProvider, useSignalR } from '@apps-common/contexts/SignalrContext'
import { IUnitTrafficStreamModel } from '@apps-common/types/portal/unit'
import { HubConnectionState } from '@microsoft/signalr'

interface IProps {
  ipAddress: string
  imei: string
  onSuccess: () => void
}

interface IStreamProps extends IProps {
  isAtTop: boolean
}

const UnitStream: React.FC<IStreamProps> = ({ ipAddress, imei, onSuccess, isAtTop }) => {
  const { connection, status } = useSignalR()
  const [messages, setMessages] = useState<IUnitTrafficStreamModel[]>([])
  const [messagesCountViewed, setMessagesCountViewed] = useState<number>(0)

  useEffect(() => {
    if (connection && status === HubConnectionState.Connected) {
      console.log('Connection State... Subscribing to RecieveMessage')

      const handleMessage = (message: any) => {
        console.log('RecieveMessage event received:', message)
        // if (!isRefetching && !isLoading) {
        setMessages((prevMessages) => [...prevMessages, message])
        if (isAtTop) {
          setMessagesCountViewed((prevCount) => prevCount + 1)
        }
        // }
      }

      // Register the event listener
      connection.on('RecieveMessage', handleMessage)

      // Cleanup the event listener on unmount or when `connection` changes
      return () => {
        connection.off('RecieveMessage', handleMessage)
      }
    }
  }, [connection])

  useEffect(() => {
    if (messages.length > 0) {
      onSuccess()
    }
  }, [messages])

  return (
    <div className='flex flex-col w-full'>
      <AnimatePresence>
        <PageContentContainerInline
          useMargin
          classes='w-full md:w-[360px] lg:w-[460px] xl:w-[510px] top-[180px] sm:top-[210px] h-[30px] fixed z-[5] text-center flex items-center justify-center'
        >
          {messages.length > messagesCountViewed && !isAtTop && (
            <motion.button
              key='new-event-messages-scroll-up'
              {...motionSlideDownFadeIn(0.35)}
              onClick={() => {
                const ele = document.getElementById('events-top-marker')

                if (ele) {
                  ele.scrollIntoView({ block: 'end', behavior: 'smooth' })
                }

                setMessagesCountViewed(messages.length)
              }}
              className='bg-secondary-content text-secondary px-[10px] py-[5px] rounded-xl shadow-lg flex items-center justify-center gap-[10px] text-[0.9rem] jost-light'
            >
              New Events
              <commonIcons.arrowRight className='text-[1rem] rotate-[-90deg]' />
            </motion.button>
          )}
        </PageContentContainerInline>
        <div className='relative flex flex-col gap-[15px]'>
          {messages.map((m, i) => (
            <motion.div
              key={`${ipAddress}-${imei}-traffic-stream-${m.id}-${messages.length - i}`}
              initial='initial'
              animate='animate'
              variants={{
                initial: {
                  top: '-20px',
                  height: 0,
                  opacity: 0,
                  transition: {
                    duration: 0.3
                  }
                },
                animate: {
                  top: '0px',
                  height: 'auto',
                  opacity: 1,
                  transition: {
                    duration: 0.3
                  }
                }
              }}
            >
              <EventItemCard key={`traffic-live-event-item-${m.id}`} event={m as any} />
            </motion.div>
          ))}
          {/* {messages?.length > 0 && (
            <motion.div
              initial='initial'
              animate='animate'
              variants={{
                initial: {
                  opacity: 0,
                  transition: {
                    duration: 0.3
                  }
                },
                animate: {
                  opacity: 1,
                  transition: {
                    duration: 0.3
                  }
                }
              }}
              className='flex items-center justify-center mt-[20px] mb-[35px] gap-[20px]'
            >
              <div className='grow h-[1px] bg-primary-content/30' />
              <div className='text-primary-content/40 jost-light text-[0.75rem] uppercase'>End of new events</div>
              <div className='grow h-[1px] bg-primary-content/30' />
            </motion.div>
          )} */}
        </div>
        {messages.length === 0 && (
          <div className='relative flex flex-col gap-[15px]'>
            <div className='flex items-center justify-center bg-secondary rounded-3xl p-4'>
              <div className='bg-secondary-content/10 py-2 px-4 rounded-3xl text-secondary-content/70 flex items-center justify-center gap-[10px] text-[0.85rem]'>
                Waiting for events
                <div className='relative'>
                  <div className='absolute top-0 left-0 bg-pending w-[15px] h-[15px] rounded-full animate-ping' />
                  <div className='bg-pending w-[15px] h-[15px] rounded-full' />
                </div>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

const UnitStreamCheck: React.FC<IProps> = ({ ipAddress, imei, onSuccess }) => {
  const { status: cs, setIdentifierStr } = useSignalR()

  useEffect(() => {
    if (ipAddress && imei) {
      setIdentifierStr(`${ipAddress},${imei}`)
    }
  }, [ipAddress, imei])

  return (
    <div className='bg-primary-content/5 rounded-3xl p-4'>
      <div className='bg-secondary flex items-center justify-between rounded-3xl p-4 mb-[20px]'>
        <div>
          <SectionTitle text='Traffic Check' classes='text-secondary-content' />
          <div className='text-[0.65rem] bg-secondary-content/10 px-[5px] rounded-full'>
            IP: {ipAddress}, IMEI: {imei}
          </div>
        </div>
        <HubConnectionStatus hubStatus={cs} />
      </div>
      <UnitStream ipAddress={ipAddress} imei={imei} onSuccess={onSuccess} isAtTop />
    </div>
  )
}

export const UnitStreamCheckView: React.FC<IProps> = ({ ipAddress, imei, onSuccess }) => (
  <SignalRProvider>
    <UnitStreamCheck ipAddress={ipAddress} imei={imei} onSuccess={onSuccess} />
  </SignalRProvider>
)
